import type { AtlasSuggestionsContext } from "@intelligems/ig-types/functions/agentic-logic/atlas-suggestions/atlas-suggestions";

import { useAtlasSuggestions } from "@src/api/ai/atlas-suggestions/use-atlas-suggestions";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";

import {
  SuggestionChipRow,
  SuggestionChipSkeletonRow,
} from "./SuggestionChipRow";
import { useRailComposerCardOpen } from "./railComposerCard";

/**
 * Atlas's suggestions for the screen its rail sits beside (ENG-7644): rule
 * chips on a rule's screens (per tab — the Advanced tab gets three), chips for
 * starting a rule on the rules list, experience chips on an experience's
 * screens. They are generated server-side for that context and cached there
 * and here, so the row costs one generation per context.
 *
 * Never in the rail's way: chip-shaped skeletons hold the row while it loads,
 * failures render nothing, and the row steps aside for a parked card ask and
 * while Atlas is working. Clicking one sends its prompt as the merchant's
 * message.
 */
export const AtlasContextSuggestionChips = ({
  context,
}: {
  /** Where the rail is; null for a screen that offers no chips. */
  context: AtlasSuggestionsContext | null;
}) => {
  const { inputRequirement, submitInput, disabled, streaming } =
    useExecutionPanelContext();
  const suggestionsQuery = useAtlasSuggestions(context);
  const suggestions = suggestionsQuery.data?.suggestions ?? [];

  // Either card on the composer — a parked question or the rail's own cart
  // ask — sits directly on the input; the row comes back once it is dismissed
  // (ENG-7894).
  const cardOpen = useRailComposerCardOpen();
  const cardAskParked = inputRequirement.userInput.type !== "text" || cardOpen;
  if (
    context !== null &&
    suggestionsQuery.isPending &&
    !cardAskParked &&
    !disabled &&
    !streaming
  ) {
    return <SuggestionChipSkeletonRow label="Loading suggestions from Atlas" />;
  }
  if (
    context === null ||
    suggestions.length === 0 ||
    cardAskParked ||
    disabled ||
    streaming
  ) {
    return null;
  }

  return (
    <SuggestionChipRow
      label="Suggestions from Atlas"
      chips={suggestions.map((suggestion) => ({
        key: suggestion.label,
        label: suggestion.label,
        onClick: () =>
          submitInput({ type: "text", content: suggestion.prompt }),
      }))}
    />
  );
};
